import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MessageService } from 'src/app/service/message.service';
import { GeocodeHit, GeocodeOutcome, GeocodeService } from 'src/app/service/geocode.service';
import { resolveMapProvider } from 'src/app/util/geo';
import { environment } from 'src/environments/environment';
declare var naver;

/**
 * 주소 검색 + 위치 지도.
 *
 * 좌표가 국내면 네이버, 해외면 구글로 그린다(`util/geo`). 네이버는 해외 타일을 못 그려서
 * 해외 좌표를 넘기면 회색 화면이 된다.
 *
 * **해외 지도에는 드래그 핀을 켠다.** 구글이 한국어 음차 주소("오키나와현 구니가미군 …")를
 * 좌표로 바꿔 주긴 하지만 번지를 버리고 마을 중심으로 접는 경우가 있어(실측 154m 오차),
 * 담당자가 손으로 바로잡을 수단이 없으면 상품이 조용히 틀린 위치로 저장된다.
 */
@Component({
  selector: 'map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() id: string;
  @Input() latitude: number;
  @Input() longitude: number;
  @Input() address: string;
  @Input() mapOption: {
    draggable: true;
    scrollWheel: true;
  };
  @Input() placeholder = '';
  @Input() invalid: boolean;
  @Output() searchEvent = new EventEmitter();
  @ViewChild('input') input: ElementRef;
  zoom = 10;
  naverMap: any;
  /** 좌표가 근사치라 담당자가 핀을 옮겨야 하는 상태. 템플릿이 안내 문구를 띄운다. */
  approximate = false;
  /** 검색 진행 중 — 중복 클릭을 막는다(템플릿이 버튼을 비활성화한다). */
  searching = false;
  /** 국내 좌표인데 시군구를 못 채운 상태 — 저장하면 지역 필터에서 빠진다. */
  regionUnresolved = false;
  /**
   * 핀을 끌 수 있는 상태인가 — 구글 지도에서만 켜진다(네이버 마커는 드래그를 안 받는다).
   * 근사치 안내 문구가 이 값으로 갈린다. 네이버가 못 찾은 국내 주소를 구글이 근사치로 찾으면
   * provider 는 'naver' 가 되므로, 그때 "핀을 끌어주세요" 는 끌 핀이 없는 안내가 된다.
   */
  get canDragPin(): boolean {
    return this.renderedProvider === 'google';
  }
  /** 구글 지도 초기화가 실패한 상태. 팝업은 한 번 닫히면 사라지므로 템플릿에도 남긴다. */
  googleMapFailed = false;
  /** 지금 그려진 지도가 어느 provider 인지 — 좌표가 바뀌어 provider 가 갈리면 다시 그린다. */
  private renderedProvider: 'naver' | 'google' | null = null;
  private naverMarker: any;
  private googleMap: any;
  private googleMarker: any;
  /**
   * 구글 지도 초기화는 await 두 번을 지난다. 그 사이 들어온 검색이 `moveTo` 로 새면 지도는
   * 첫 주소에, 폼에 올라간 좌표는 두 번째 주소에 남는다 — 핀과 저장될 값이 어긋난다.
   * 진행 중인 초기화를 들고 있다가 끝난 뒤 최신 좌표로 다시 맞춘다.
   */
  private googleInit: Promise<void> | null = null;
  private unsubscribeAuthFailure: (() => void) | null = null;
  /** 언마운트 후 늦게 도착한 콜백이 떨어져 나간 지도를 만지지 않게 한다. */
  private destroyed = false;
  /** 첫 ngOnChanges 땐 `[id]` 가 아직 안 붙어 네이버가 "Container … not found" 로 던진다(NNB-ANGULAR-C8). */
  private viewReady = false;
  /** provider 를 바꿀 때마다 오른다. 늦게 끝난 구글 초기화가 이미 바뀐 지도를 덮지 않게 한다. */
  private renderSeq = 0;
  /** 마지막 지오코딩 결과. 핀을 끌었을 때 좌표만 갈아 끼워 다시 올려보낸다. */
  private lastHit: GeocodeHit | null = null;

  constructor(
    private messageService: MessageService,
    private geocodeService: GeocodeService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // 좌표가 바깥에서 바뀌면(AI 제안 적용 등) 다시 그린다. 예전엔 resize 만 쏘고 말았는데,
    // provider 가 하나였을 땐 중심만 낡는 정도였지만 지금은 **해외 좌표에 네이버 지도가
    // 남는다** — 이 기능이 없애려던 회색 타일이 그대로 재현된다.
    if (this.viewReady && (changes['latitude'] || changes['longitude'])) this.renderMap();
    window.dispatchEvent(new Event('resize'));
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.renderMap();
    window.dispatchEvent(new Event('resize'));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.unsubscribeAuthFailure?.();
  }

  /** 현재 좌표에 맞는 provider 로 지도를 그린다. provider 가 바뀌면 컨테이너를 비우고 새로 만든다. */
  private renderMap(): void {
    const provider = resolveMapProvider(this.latitude, this.longitude);
    // 좌표가 없으면 그릴 수 없다. 예전에는 (0,0) 이 그대로 넘어와 적도 앞바다를 그렸다.
    if (!provider) return;
    if (provider === this.renderedProvider) {
      // 초기화가 아직 진행 중이면 지금 moveTo 해 봐야 대상이 없다 — 끝난 뒤에 맞춘다.
      if (this.googleInit) {
        void this.googleInit.then(() => {
          if (!this.destroyed) this.moveTo(this.latitude, this.longitude);
        });
        return;
      }
      this.moveTo(this.latitude, this.longitude);
      return;
    }

    // provider 가 바뀌면 앞선 지도가 남긴 DOM 을 걷어낸다(네이버/구글이 같은 컨테이너를 쓴다).
    const container = document.getElementById(this.id);
    if (container) container.replaceChildren();
    this.naverMap = null;
    this.naverMarker = null;
    this.googleMap = null;
    this.googleMarker = null;
    this.googleInit = null;
    this.renderedProvider = null;
    this.renderSeq++;
    // 실패 상태는 provider 를 바꿀 때 함께 지운다 — 안 지우면 구글 실패 뒤 국내 주소를 검색해
    // 네이버 지도가 멀쩡히 떠도 "지도를 불러오지 못했습니다" 배너가 그 위에 남는다.
    this.googleMapFailed = false;
    this.unsubscribeAuthFailure?.();
    this.unsubscribeAuthFailure = null;

    // 네이버는 생성 성공 뒤에, 구글은 초기화 중 호출이 googleInit 을 기다리도록 먼저 적는다(실패 시 되돌림).
    if (provider === 'naver') {
      this.initialNaverMap(this.latitude, this.longitude);
      this.renderedProvider = 'naver';
    } else {
      this.renderedProvider = 'google';
      this.googleInit = this.initialGoogleMap(this.latitude, this.longitude);
    }
  }

  /** 이미 그려진 지도를 새 좌표로 옮긴다. */
  private moveTo(lat: number, lon: number): void {
    if (this.renderedProvider === 'naver' && this.naverMap) {
      this.naverMarker?.setMap(null);
      this.naverMap.setCenter(new naver.maps.Point(lon, lat));
      this.naverMap.setZoom(17);
      this.naverMarker = new naver.maps.Marker({ position: new naver.maps.LatLng(lat, lon), map: this.naverMap });
      return;
    }
    if (this.renderedProvider === 'google' && this.googleMap && this.googleMarker) {
      const position = { lat, lng: lon };
      this.googleMap.setCenter(position);
      this.googleMap.setZoom(16);
      this.googleMarker.position = position;
    }
  }

  /** 인자 순서는 initialGoogleMap 과 맞춘다(lat, lon) — 예전엔 (lon, lat) 이라 뒤바꾸기 쉬웠다. */
  private initialNaverMap(lat: number, lon: number) {
    let mapOptions = {
      center: new naver.maps.LatLng(lat, lon),
      zoom: this.zoom,
      zoomControl: true,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: naver.maps.MapTypeControlStyle.BUTTON,
        position: naver.maps.Position.TOP_RIGHT,
      },
      zoomControlOptions: {
        style: naver.maps.ZoomControlStyle.SMALL,
        position: naver.maps.Position.TOP_RIGHT,
      },
    };
    mapOptions = { ...mapOptions, ...this.mapOption };
    this.naverMap = new naver.maps.Map(this.id, mapOptions);

    const point = new naver.maps.Point(lon, lat);
    this.naverMap.setCenter(point);
    this.naverMarker = new naver.maps.Marker({
      position: new naver.maps.LatLng(lat, lon),
      map: this.naverMap,
    });

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }

  private async initialGoogleMap(lat: number, lon: number): Promise<void> {
    const container = document.getElementById(this.id);
    if (!container) {
      // 아래 catch 와 같은 이유로 provider 를 되돌린다 — 'google' 로 남겨 두면 다음 검색이
      // moveTo 로 새고 googleMap 이 null 이라 아무 일도 하지 않는다(무증상 고착).
      this.renderedProvider = null;
      return;
    }
    const seq = this.renderSeq;
    const stale = () => this.destroyed || seq !== this.renderSeq;
    try {
      const maps = await this.geocodeService.loadGoogleMaps();
      if (stale()) return;
      const position = { lat, lng: lon };
      this.googleMap = new maps.Map(container, {
        center: position,
        zoom: 16,
        mapId: environment.googleMaps?.mapId,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
      const { AdvancedMarkerElement } = await maps.importLibrary('marker');
      if (stale()) return;
      this.googleMarker = new AdvancedMarkerElement({
        map: this.googleMap,
        position,
        gmpDraggable: true,
        // 없으면 마커가 Tab 으로 도달하지 않는다 — 근사치 좌표를 바로잡는 유일한 수단이
        // 드래그 핀인데 위도/경도 입력칸이 없어, 키보드만 쓰는 담당자는 방법이 없어진다.
        gmpClickable: true,
      });
      this.googleMapFailed = false;
      // 키·리퍼러 거절은 여기까지 예외로 오지 않는다 — 지도가 뜬 뒤 타일 요청에서 비동기로
      // 거절되고 회색 상자만 남는다. 전역 통로로만 알 수 있어 따로 구독한다.
      this.unsubscribeAuthFailure?.();
      this.unsubscribeAuthFailure = this.geocodeService.onAuthFailure(() => {
        this.googleMapFailed = true;
      });
      this.googleMarker.addListener('dragend', () => {
        const pos = this.googleMarker.position;
        if (!pos) return;
        const nextLat = typeof pos.lat === 'function' ? pos.lat() : pos.lat;
        const nextLon = typeof pos.lng === 'function' ? pos.lng() : pos.lng;
        this.latitude = nextLat;
        this.longitude = nextLon;
        // 담당자가 직접 찍은 좌표가 지오코더 결과보다 정확하다 — 근사치 안내를 내린다.
        this.approximate = false;
        this.emitCurrent();
      });
      setTimeout(() => window.dispatchEvent(new Event('resize')));
    } catch (error) {
      if (stale()) return;
      // **provider 를 되돌리는 것이 핵심이다.** 'google' 로 남겨 두면 다음 검색이 위
      // `provider === this.renderedProvider` 단축 경로를 타고 moveTo 로 새는데, 거기 가드는
      // googleMap 이 null 이라 아무 일도 하지 않는다 — 팝업 한 번 뜨고 그 뒤로는 지도가
      // 영영 비어 있으면서 에러 메시지도 안 나온다(담당자에겐 앱이 멈춘 것처럼 보인다).
      this.renderedProvider = null;
      this.googleMapFailed = true;
      console.error('[MapComponent] 구글 지도 초기화 실패', error);
      this.messageService.popup({
        title: '지도',
        description:
          '구글 지도를 불러오지 못했습니다. 좌표는 검색 결과로 저장되지만 핀을 끌어 위치를 보정할 수 없습니다.',
        cancelLabel: '확인',
      });
    }
  }

  /** 현재 좌표와 마지막 지오코딩 상세를 상위 폼으로 올려보낸다. */
  private emitCurrent(): void {
    this.searchEvent.emit({
      latitude: this.latitude,
      longitude: this.longitude,
      address: this.address,
      country: this.lastHit?.country ?? '',
      sido: this.lastHit?.sido ?? '',
      sigugun: this.lastHit?.sigugun ?? '',
      dongmyun: this.lastHit?.dongmyun ?? '',
      ri: this.lastHit?.ri ?? '',
      rest: this.lastHit?.rest ?? '',
    });
  }

  async search(address: string) {
    // 중복 클릭 가드 — 없으면 두 번 눌렀을 때 느린 쪽이 나중에 도착해 먼저 누른 결과를 덮는다.
    if (this.searching) return;
    this.searching = true;
    // 직전 검색의 경고를 지운다 — 남겨 두면 실패한 검색에 옛 좌표의 경고가 붙어 보인다.
    this.approximate = false;
    this.regionUnresolved = false;
    let outcome: GeocodeOutcome;
    try {
      outcome = await this.geocodeService.geocode(address);
    } catch (e) {
      // catch 가 없으면 버튼만 다시 살아나고 아무 안내도 없다 — 담당자에겐 죽은 버튼과 같다.
      // 역지오코딩 어댑터가 SDK 객체를 executor 안에서 만들므로 실제로 reject 가 가능하다.
      console.error('[MapComponent] 주소 검색 실패', e);
      this.messageService.popup({
        title: '주소 검색',
        description: '주소 검색 중 오류가 발생했습니다.',
        cancelLabel: '확인',
      });
      return;
    } finally {
      this.searching = false;
    }

    if (outcome.kind === 'error') {
      this.input.nativeElement.value = this.address;
      this.messageService.popup({
        title: '주소 검색',
        description: '주소 검색 중 오류가 발생했습니다.',
        cancelLabel: '확인',
      });
      return;
    }
    if (outcome.kind === 'empty') {
      this.input.nativeElement.value = this.address;
      this.messageService.popup({
        title: '주소 검색',
        description: '검색 결과가 없습니다.',
        cancelLabel: '확인',
      });
      return;
    }

    // 검색 중에 담당자가 입력칸을 고쳤으면 옛 응답을 새 주소에 붙이지 않는다.
    if (this.input.nativeElement.value.trim() !== address.trim()) {
      console.warn('[MapComponent] 검색 중 주소가 바뀌어 결과를 버린다', { requested: address });
      return;
    }

    const hit = outcome.hits[0];
    this.lastHit = hit;
    this.latitude = hit.latitude;
    this.longitude = hit.longitude;
    // **this.address 는 반드시 갱신한다** — emitCurrent 가 이 값을 폼으로 올려보내고, 그것이
    // 담당자가 친 텍스트가 저장에 도달하는 유일한 경로다(입력칸은 단방향 [value] 바인딩이라
    // (input) 핸들러가 없다). 갱신을 건너뛰면 좌표는 새 위치, 주소는 옛 위치로 저장된다.
    //
    // 반면 **입력칸 되쓰기는 조건부**다. 해외·상호명 결과는 normalizedAddress 가 null 이라
    // 정규화된 표기가 없는데, 그때 this.address 를 되쓰면 방금 친 글자가 화면에서 사라진다.
    this.address = hit.normalizedAddress ?? address;
    if (hit.normalizedAddress) this.input.nativeElement.value = this.address;
    this.approximate = hit.approximate;
    this.regionUnresolved = hit.regionUnresolved;

    this.emitCurrent();
    this.renderMap();

    if (hit.regionUnresolved) {
      this.messageService.popup({
        title: '지역 정보 미확인',
        description:
          '시·군·구를 채우지 못했습니다. 이대로 저장하면 지도 검색의 지역 필터에 노출되지 않습니다. 주소를 더 정확히 입력한 뒤 다시 검색해주세요.',
        cancelLabel: '확인',
      });
    }
    if (hit.approximate) {
      this.messageService.popup({
        title: '대략적인 위치',
        description: '번지까지 찾지 못했습니다. 지도에서 핀을 끌어 정확한 위치로 옮겨주세요.',
        cancelLabel: '확인',
      });
    }
  }
}
